import React from 'react';
import { MdClose } from 'react-icons/md';
import { FileText, Globe, Lock } from 'lucide-react';

import useScrollLock from '../../../hooks/useScrollLock';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store/hooks';
import { createTeamNote } from '../../../store/slices/teamFocusSession/noteThunks';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { noteFormSchema, NoteForm } from '@/schemas/note';

interface NoteModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialNote?: any; // Using any for now to avoid import issues or use Note if imported
}

import { updateTeamNote } from '../../../store/slices/teamFocusSession/noteThunks';

const NoteModal: React.FC<NoteModalProps> = ({ isOpen, onClose, initialNote }) => {
    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { isSubmitting },
    } = useForm<NoteForm>({
        resolver: zodResolver(noteFormSchema),
        defaultValues: {
            note: '',
            isPublic: false,
        }
    });

    const isPublic = watch('isPublic');
    const noteText = watch('note');

    const { teamId } = useParams<{ teamId: string }>();
    const dispatch = useAppDispatch();

    useScrollLock(isOpen);

    React.useEffect(() => {
        if (isOpen) {
            reset({
                note: initialNote?.note || '',
                isPublic: initialNote?.is_public ?? false,
            });
        }
    }, [isOpen, reset, initialNote]);

    const onFormSubmit = async (data: NoteForm) => {
        if (!teamId) return;

        try {
            if (initialNote) {
                await dispatch(
                    updateTeamNote({
                        teamId,
                        noteId: initialNote.id,
                        updates: { note: data.note.trim(), is_public: data.isPublic }
                    }),
                ).unwrap();
                toast.success("Note updated successfully!");
            } else {
                await dispatch(
                    createTeamNote({ teamId, note: data.note.trim(), is_public: data.isPublic }),
                ).unwrap();
                toast.success("Note added successfully!");
            }
            onClose();
        } catch (err) {
            console.error("Note action failed", err);
            toast.error(initialNote ? "Failed to update note" : "Failed to add note");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-[#FFF8F0] rounded-2xl p-6 sm:p-8 max-w-lg w-full border border-gray-200/60 shadow-xl">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-orange-600" />
                        <h3 className="text-2xl font-medium text-gray-900">
                            {initialNote ? 'Edit Note' : 'New Note'}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl transition-colors"
                    >
                        <MdClose />
                    </button>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            What's on your mind?
                        </label>
                        <textarea
                            {...register('note')}
                            placeholder="Write your note here..."
                            className="w-full px-4 py-3 bg-white border border-gray-200/60 rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300 text-base min-h-[150px] resize-none"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.ctrlKey) {
                                    handleSubmit(onFormSubmit)();
                                }
                            }}
                        />

                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                            {isPublic ? <Globe className="w-4 h-4 text-gray-500" /> : <Lock className="w-4 h-4 text-gray-500" />}
                            Visibility
                        </label>
                        <div className="flex items-center gap-3 mt-2">
                            <button
                                type="button"
                                onClick={() => setValue('isPublic', true)}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${isPublic
                                    ? 'bg-orange-50 text-orange-700 border border-orange-200 relative z-10'
                                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                Team
                            </button>
                            <button
                                type="button"
                                onClick={() => setValue('isPublic', false)}
                                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${!isPublic
                                    ? 'bg-orange-50 text-orange-700 border border-orange-200 relative z-10'
                                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                Private
                            </button>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-3 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 rounded-xl font-medium transition-all duration-300 text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit(onFormSubmit)}
                            disabled={!noteText?.trim() || isSubmitting}
                            className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-300 text-sm ${!noteText?.trim() || isSubmitting
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-orange-600 hover:bg-orange-700 text-white'
                                }`}
                        >
                            {isSubmitting
                                ? (initialNote ? 'Saving...' : 'Adding...')
                                : (initialNote ? 'Save Changes' : 'Add Note')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NoteModal;
